/**
 * Now and Inbox as first-class cross-project surfaces (docs/ux/project-tab-spec.md §1.9): row
 * models, grouping, filters and keyboard triage. Pure joins of the hub's Inbox items and runs
 * with the decrypted decision entities; nothing is stored, so nothing goes stale.
 */
import { type DecisionRecord, isRiskyDecisionCategory } from '@viko/protocol'
import { AGENT_NAME, RUN_PHASE_LABEL, type RunPhase } from '@viko/transcript-ui'
import { type DecisionView, viewFromDecision, viewFromLegacy } from './decision-view.js'
import { isSnoozed, leverage, type Snoozes } from './inbox.js'
import { type Freshness, isBlockerItem, isLandingFailure, stallReason } from './inbox-freshness.js'
import { isLiveRun, itemProject, type LivePhase, summaryPhase } from './lanes-model.js'
import { isBlockedRow } from './needs-you.js'
import type { InboxItem, Machine, Project, RunSummary } from './types.js'

// ---- Inbox ------------------------------------------------------------------------------------

export type InboxKind =
  | 'decision'
  | 'blocked'
  | 'approval'
  | 'landing'
  | 'release'
  | 'proposal'
  | 'rule'
  | 'build'
  | 'alert'
  | 'card'

export const KIND_LABEL: Record<InboxKind, string> = {
  decision: 'Decision',
  blocked: 'Blocked',
  approval: 'Approval',
  landing: 'Landing',
  release: 'Release',
  proposal: 'Proposed tasks',
  rule: 'Rule proposal',
  build: 'Build',
  alert: 'Alert',
  card: 'Update',
}

export interface QuickPick {
  id: string
  key: number
  label: string
  suggested: boolean
}

export interface InboxRow {
  /** Inspector key: `decision:<id>` or `ask:<hub item id>`. */
  key: string
  kind: InboxKind
  title: string
  projectId?: string
  projectName?: string
  lane?: string
  workstreamId?: string
  agentLabel?: string
  /** Needs-you reason chips: category, blocks run / unblocks n. */
  reasons: string[]
  blocking: boolean
  unblocks: number
  /** The tasks `unblocks` counts, when known (machine blockers: the blocked run's task). */
  unblocksTasks?: { taskId: string; title?: string }[]
  createdAt: number
  score: number
  suggested?: { id: string; label: string }
  /** Simple, non-risky asks only. */
  quick?: QuickPick[]
  /** Landing or release chip. */
  chip?: string
  locked: boolean
  /** Never joins a bulk action without a passkey. */
  risky: boolean
  view?: DecisionView
  item?: InboxItem
  // ---- inbox-detail: re-evaluated against the newest reports ----
  /** "May be outdated…" / "Waiting for a fresh report from Mac" (shown on the row). */
  note?: string
  stale?: boolean
  /** Freshness says "may be outdated": lists collapse these under one line ("2 may be outdated"). */
  outdated?: boolean
  freshness?: Freshness
  // ---- end inbox-detail ----
}

export interface InboxInput {
  decisions: readonly DecisionRecord[]
  items: readonly InboxItem[]
  runs: readonly RunSummary[]
  projects: readonly Pick<Project, 'id' | 'name'>[]
  machines: readonly Pick<Machine, 'id' | 'name'>[]
  snoozes?: Snoozes
  now: number
  /** The app's project id for a decision (identity on encrypted accounts). */
  projectOf?: (d: DecisionRecord) => string
  /** inbox-detail: re-evaluates stall / landing items; resolved ones leave the Inbox. */
  freshness?: (i: InboxItem) => Freshness | undefined
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`

/** The hub ask a decision entity belongs to (the same `viko_ask`). */
export function matchesDecision(i: InboxItem, d: DecisionRecord): boolean {
  if (i.type !== 'question') return false
  if (i.event?.kind === 'question' && i.event.decisionId) return i.event.decisionId === d.id
  return !!d.runId && i.runId === d.runId && (i.id === d.questionId || (!!d.seq && i.seq === d.seq))
}

/** `body.type` of hub cards that only inform (audit trail), never ask. */
const INFO_NOTICE_TYPES = new Set([
  'content-unlock',
  'device-added',
  'device-removed',
  'device-revoked',
])

/** An audit / info notice: shown as a dismissible line and under You › Security, never counted. */
export function isInfoNotice(i: Pick<InboxItem, 'body' | 'blocking'>): boolean {
  const t = (i.body as { type?: unknown } | undefined)?.type
  return typeof t === 'string' && INFO_NOTICE_TYPES.has(t) && !i.blocking
}

function kindOf(i: InboxItem): InboxKind {
  if (i.type === 'question') return 'decision'
  if (isBlockerItem(i)) return 'blocked'
  if (isLandingFailure(i)) return 'landing'
  if (i.type === 'approval') return 'approval'
  if (i.type === 'land') return i.category === 'ship-tap' ? 'release' : 'landing'
  if (i.type === 'build') return 'build'
  if (i.kind === 'proposal' || (i.kind === 'decision' && i.category === 'triage')) return 'proposal'
  if (i.kind === 'rule') return 'rule'
  if (i.category === 'device-alert') return 'alert'
  return 'card'
}

function agentText(
  runId: string | undefined,
  runs: ReadonlyMap<string, RunSummary>,
  machines: readonly Pick<Machine, 'id' | 'name'>[],
  agent?: 'claude' | 'codex',
  machineId?: string,
) {
  const r = runId ? runs.get(runId) : undefined
  const who = r?.agent ?? agent
  if (!who) return undefined
  const m = machines.find((x) => x.id === (r?.machineId ?? machineId))
  return `${AGENT_NAME[who]}${m ? ` on ${m.name}` : ''}`
}

function reasonsOf(v: Pick<InboxRow, 'blocking' | 'unblocks'>, first?: string): string[] {
  const out = first ? [first] : []
  if (v.blocking) out.push('Blocks a run')
  if (v.unblocks > 0) out.push(`Unblocks ${plural(v.unblocks, 'task')}`)
  return out
}

function quickOf(v: DecisionView): QuickPick[] | undefined {
  if (!v.quickPick || !v.open) return undefined
  return v.options.map((o) => ({
    id: o.id,
    key: o.key,
    label: o.label,
    suggested: o.recommended,
  }))
}

export function buildInboxRows(input: InboxInput): {
  rows: InboxRow[]
  snoozed: InboxRow[]
  /** Delegated decisions the agents resolved in the last day (optional review). */
  assumptions: DecisionRecord[]
  /** Items whose condition no longer holds (the caller marks them done once). */
  resolved: InboxItem[]
  /** Audit / info notices (unlocked via, device added…): never rows, never counted. */
  notices: InboxItem[]
} {
  const { now, machines } = input
  const snoozes = input.snoozes ?? {}
  const runs = new Map(input.runs.map((r) => [r.runId, r]))
  const pname = (id?: string) => (id ? input.projects.find((p) => p.id === id)?.name : undefined)
  const used = new Set<InboxItem>()
  const rows: InboxRow[] = []
  const snoozed: InboxRow[] = []
  const assumptions: DecisionRecord[] = []
  const resolved: InboxItem[] = []
  const notices: InboxItem[] = []

  for (const d of input.decisions) {
    if (d.status === 'resolved' && d.resolution?.assumption && now - d.resolution.at < 86_400_000)
      assumptions.push(d)
    const item = input.items.find((i) => matchesDecision(i, d))
    if (item) used.add(item)
    if (d.status !== 'open') continue
    const v = viewFromDecision(d, now, item)
    const score =
      item?.score ??
      leverage(
        {
          ...(item ?? {}),
          blocking: v.blocking,
          unblocks: v.unblocks,
          createdAt: d.createdAt,
          score: 0,
        } as InboxItem,
        now,
      )
    const run = d.runId ? runs.get(d.runId) : undefined
    const projectId = input.projectOf?.(d) ?? d.projectId
    const lane = item?.workstreamName ?? run?.workstreamName
    const agentLabel = agentText(d.runId, runs, machines, d.agent, d.machineId)
    const row: InboxRow = {
      key: v.key,
      kind: 'decision',
      title: v.title,
      projectId,
      ...(pname(projectId) ? { projectName: pname(projectId) } : {}),
      ...(lane ? { lane } : {}),
      ...(d.workstreamId ? { workstreamId: d.workstreamId } : {}),
      ...(agentLabel ? { agentLabel } : {}),
      reasons: reasonsOf(v, v.categoryLabel),
      blocking: v.blocking,
      unblocks: v.unblocks,
      createdAt: d.createdAt,
      score,
      ...(v.suggested ? { suggested: { id: v.suggested.id, label: v.suggested.label } } : {}),
      ...(quickOf(v) ? { quick: quickOf(v) } : {}),
      locked: false,
      risky: isRiskyDecisionCategory(d.category),
      view: v,
      ...(item ? { item } : {}),
    }
    const hidden = (d.snoozedUntil ?? 0) > now || (item ? isSnoozed(snoozes, item.id, now) : false)
    ;(hidden ? snoozed : rows).push(row)
  }

  for (const i of input.items) {
    if (used.has(i)) continue
    if (isInfoNotice(i)) {
      if (!isSnoozed(snoozes, i.id, now)) notices.push(i)
      continue
    }
    const fresh = input.freshness?.(i)
    if (fresh?.state === 'resolved') {
      resolved.push(i)
      continue
    }
    const kind = kindOf(i)
    const projectId = itemProject(i, runs, input.projects)
    const v = kind === 'decision' ? viewFromLegacy(i, now) : undefined
    const blocking = i.blocking
    // A machine blocker unblocks only the task(s) of the run it holds up, never a bare count.
    const blockedRun = kind === 'blocked' ? runs.get(i.runId) : undefined
    const unblocksTasks =
      kind === 'blocked'
        ? blockedRun?.taskId
          ? [
              {
                taskId: blockedRun.taskId,
                ...(blockedRun.taskTitle ? { title: blockedRun.taskTitle } : {}),
              },
            ]
          : []
        : undefined
    const unblocks = unblocksTasks ? unblocksTasks.length : (i.unblocks ?? 0)
    const agentLabel = agentText(i.runId || undefined, runs, machines, i.agent, i.machineId)
    const chip =
      kind === 'landing' && i.land
        ? `Land ${i.land.branch}: ${plural(i.land.commits, 'commit')}`
        : kind === 'release'
          ? 'Release waits for a passkey'
          : undefined
    const row: InboxRow = {
      key: `ask:${i.itemId ?? i.id}`,
      kind,
      title: v
        ? v.title
        : kind === 'landing' && i.land
          ? `Land ${i.land.branch}?`
          : fresh?.blocker
            ? `Blocked: ${fresh.blocker.title}`
            : kind === 'blocked' && fresh?.machine
              ? `Blocked: environment on ${fresh.machine.name}`
              : fresh?.landing
                ? `Landing failed: ${fresh.landing.branch ?? 'branch'} (${fresh.landing.failure?.phaseLabel ?? fresh.landing.reason ?? 'no reason reported'})`
                : i.title,
      ...(projectId ? { projectId } : {}),
      ...((pname(projectId) ?? i.projectName)
        ? { projectName: pname(projectId) ?? i.projectName }
        : {}),
      ...(i.workstreamName ? { lane: i.workstreamName } : {}),
      ...(i.workstreamId ? { workstreamId: i.workstreamId } : {}),
      ...(agentLabel ? { agentLabel } : {}),
      reasons: reasonsOf(
        { blocking, unblocks },
        kind === 'decision'
          ? undefined
          : kind === 'blocked'
            ? (stallReason(fresh?.blocker?.cause ?? i.category) ?? KIND_LABEL.blocked)
            : KIND_LABEL[kind],
      ),
      blocking,
      unblocks,
      ...(unblocksTasks?.length ? { unblocksTasks } : {}),
      createdAt: i.createdAt,
      score: leverage(i, now),
      ...(v?.suggested ? { suggested: { id: v.suggested.id, label: v.suggested.label } } : {}),
      ...(v && quickOf(v) ? { quick: quickOf(v) } : {}),
      ...(chip ? { chip } : {}),
      locked: !!i.locked,
      risky: kind === 'landing' || kind === 'release',
      ...(v ? { view: v } : {}),
      item: i,
      ...(fresh
        ? {
            freshness: fresh,
            ...(fresh.state === 'outdated' || fresh.state === 'waiting'
              ? { note: fresh.text, stale: true }
              : {}),
            ...(fresh.state === 'outdated' ? { outdated: true } : {}),
          }
        : {}),
    }
    ;(isSnoozed(snoozes, i.id, now) ? snoozed : rows).push(row)
  }
  const rank = (a: InboxRow, b: InboxRow) =>
    Number(b.blocking) - Number(a.blocking) || b.score - a.score || a.createdAt - b.createdAt
  return { rows: rows.sort(rank), snoozed: snoozed.sort(rank), assumptions, resolved, notices }
}

/** The badge: blocking rows (the shell's `blockingCount` over the same items). */
export const inboxBlockingCount = (rows: readonly InboxRow[]) =>
  rows.filter((r) => r.blocking).length

export interface InboxFilter {
  projectId?: string
  kind?: InboxKind
  blockingOnly?: boolean
}
export function filterInboxRows(rows: readonly InboxRow[], f: InboxFilter): InboxRow[] {
  return rows.filter(
    (r) =>
      (!f.projectId || r.projectId === f.projectId) &&
      (!f.kind || r.kind === f.kind) &&
      (!f.blockingOnly || r.blocking),
  )
}

export type InboxEntry =
  | { type: 'row'; row: InboxRow }
  | { type: 'session'; id: string; title: string; rows: InboxRow[]; quick: boolean }

export interface InboxGroupView {
  key: string
  title: string
  hot: boolean
  entries: InboxEntry[]
}

/** ≥2 open decisions from one lane (or run) in a group become one "decide all" session. */
function batch(rows: InboxRow[]): InboxEntry[] {
  const sessions = new Map<string, InboxRow[]>()
  for (const r of rows) {
    if (r.kind !== 'decision' || r.locked || r.view?.multiQuestion) continue
    const k = r.workstreamId ?? r.view?.runId
    if (!k) continue
    sessions.set(k, [...(sessions.get(k) ?? []), r])
  }
  const out: InboxEntry[] = []
  const done = new Set<string>()
  for (const r of rows) {
    const k = r.kind === 'decision' ? (r.workstreamId ?? r.view?.runId) : undefined
    const s = k ? sessions.get(k) : undefined
    if (k && s && s.length >= 2) {
      if (done.has(k)) continue
      done.add(k)
      const first = s[0] as InboxRow
      const where = [first.projectName, first.lane].filter(Boolean).join(' / ') || 'This lane'
      out.push({
        type: 'session',
        id: k,
        title: `${where}: ${s.length} decisions in one pass`,
        rows: s,
        quick: s.every((x) => !!x.quick),
      })
    } else out.push({ type: 'row', row: r })
  }
  return out
}

export function groupInboxRows(
  rows: readonly InboxRow[],
  mode: 'leverage' | 'project',
  tabOrder: readonly string[],
): InboxGroupView[] {
  if (mode === 'leverage') {
    const g: [string, string, boolean, InboxRow[]][] = [
      // The canonical needs-you rows (lib/needs-you.ts): Blocked + Needs you equal every badge.
      // Blocked (can't continue until fixed) gets its own group at the top.
      ['blocked', 'Blocked', true, rows.filter((r) => r.blocking && isBlockedRow(r))],
      ['blocking', 'Needs you', true, rows.filter((r) => r.blocking && !isBlockedRow(r))],
      ['unblocks', 'Unblocks other work', false, rows.filter((r) => !r.blocking && r.unblocks > 0)],
      ['later', 'When convenient', false, rows.filter((r) => !r.blocking && !r.unblocks)],
    ]
    return g
      .filter(([, , , list]) => list.length)
      .map(([key, title, hot, list]) => ({ key, title, hot, entries: batch(list) }))
  }
  const byProject = new Map<string, InboxRow[]>()
  for (const r of rows) {
    const k = r.projectId ?? r.projectName ?? '_'
    byProject.set(k, [...(byProject.get(k) ?? []), r])
  }
  const pos = (k: string) => {
    const i = tabOrder.indexOf(k)
    return i < 0 ? Number.POSITIVE_INFINITY : i
  }
  return [...byProject.entries()]
    .sort(([a], [b]) => pos(a) - pos(b) || a.localeCompare(b))
    .map(([key, list]) => ({
      key,
      title: list[0]?.projectName ?? 'Other',
      hot: list.some((r) => r.blocking),
      entries: batch(list),
    }))
}

/** Keyboard order: a session header, then its rows; then the next entry. */
export function flatKeys(groups: readonly InboxGroupView[]): string[] {
  return groups.flatMap((g) =>
    g.entries.flatMap((e) =>
      e.type === 'row' ? [e.row.key] : [`batch:${e.id}`, ...e.rows.map((r) => r.key)],
    ),
  )
}

// ---- Now --------------------------------------------------------------------------------------

export type NowFamily = 'attention' | 'progress' | 'working' | 'idle' | 'review'

export const FAMILY_TITLE: Record<NowFamily, string> = {
  attention: 'Needs attention',
  progress: 'Making progress',
  working: 'Working',
  idle: 'Idle',
  review: 'Finished, awaiting review',
}
const FAMILY_ORDER: NowFamily[] = ['attention', 'progress', 'working', 'idle', 'review']

export function phaseFamily(p: RunPhase): NowFamily {
  switch (p) {
    case 'stalled':
    case 'looping':
    case 'blocked-environment':
    case 'waiting':
    case 'failed':
    case 'offline':
      return 'attention'
    case 'making-progress':
      return 'progress'
    case 'working':
    case 'syncing':
    case 'not-started':
      return 'working'
    case 'idle':
      return 'idle'
    default:
      return 'review'
  }
}

export interface NowRow {
  key: string
  runId: string
  title: string
  projectId?: string
  projectName?: string
  lane?: string
  agent: 'claude' | 'codex'
  agentLabel: string
  machineId: string
  liveLine: string
  phase: RunPhase
  phaseLabel: string
  family: NowFamily
  lastActivity: number
  startedAt?: number
  chip?: { label: string; tone: string }
  /** A waiting run's open decision. */
  decisionKey?: string
  live: boolean
}

export interface NowInput {
  runs: readonly RunSummary[]
  machines: readonly Pick<Machine, 'id' | 'name' | 'online'>[]
  projects: readonly Pick<Project, 'id' | 'name'>[]
  decisions?: readonly DecisionRecord[]
  live?: ReadonlyMap<string, LivePhase>
  chips?: ReadonlyMap<string, { label: string; tone: string }>
  /** Provider account label per run (shown when a machine has more than one). */
  accountOf?: (r: RunSummary) => string | undefined
  now: number
}

/** Finished runs stay on Now for review until they land (or go quiet for this long). */
const REVIEW_WINDOW = 24 * 3_600_000

export function buildNowRows(input: NowInput): NowRow[] {
  const out: NowRow[] = []
  for (const r of input.runs) {
    if (r.source === 'imported') continue
    const live = isLiveRun(r)
    const last = r.updatedAt ?? r.startedAt ?? 0
    if (!live) {
      if (r.landing?.state === 'landed') continue
      if (input.now - last > REVIEW_WINDOW) continue
    }
    const m = input.machines.find((x) => x.id === r.machineId)
    const online = m?.online ?? true
    const lp = input.live?.get(r.runId)
    const phase: RunPhase = live && !online ? 'offline' : (lp?.phase ?? summaryPhase(r, online))
    const agent = r.agent ?? 'claude'
    const d = input.decisions?.find((x) => x.runId === r.runId && x.status === 'open')
    const line =
      (online ? lp?.line : undefined) ??
      (r.status === 'waiting' && r.blockedOn
        ? `Waiting for you: ${r.blockedOn}`
        : phase === 'offline'
          ? `${m?.name ?? 'Machine'} is offline`
          : (r.activity ?? RUN_PHASE_LABEL[phase]))
    const project = input.projects.find((p) => p.id === r.projectId)
    const chip = input.chips?.get(r.runId)
    out.push({
      key: `run:${r.runId}`,
      runId: r.runId,
      title: r.title ?? r.taskTitle ?? r.runId,
      ...(r.projectId ? { projectId: r.projectId } : {}),
      ...((project?.name ?? r.projectName) ? { projectName: project?.name ?? r.projectName } : {}),
      ...(r.workstreamName ? { lane: r.workstreamName } : {}),
      agent,
      agentLabel: `${AGENT_NAME[agent]}${m ? ` on ${m.name}` : ''}${input.accountOf?.(r) ? `, ${input.accountOf(r)}` : ''}`,
      machineId: r.machineId,
      liveLine: line,
      phase,
      phaseLabel: r.typingElsewhere ? 'In a terminal' : (lp?.label ?? RUN_PHASE_LABEL[phase]),
      family: phaseFamily(phase),
      lastActivity: last,
      ...(r.startedAt ? { startedAt: r.startedAt } : {}),
      ...(chip ? { chip } : {}),
      ...(d ? { decisionKey: `decision:${d.id}` } : {}),
      live,
    })
  }
  return out
}

export interface NowGroupView {
  key: string
  title: string
  hot: boolean
  rows: NowRow[]
}

export function groupNowRows(
  rows: readonly NowRow[],
  mode: 'attention' | 'project',
  tabOrder: readonly string[],
): NowGroupView[] {
  const byRecent = (a: NowRow, b: NowRow) => b.lastActivity - a.lastActivity
  if (mode === 'attention')
    return FAMILY_ORDER.map((f) => ({
      key: f,
      title: FAMILY_TITLE[f],
      hot: f === 'attention',
      rows: rows.filter((r) => r.family === f).sort(byRecent),
    })).filter((g) => g.rows.length)
  const pos = (k: string) => {
    const i = tabOrder.indexOf(k)
    return i < 0 ? Number.POSITIVE_INFINITY : i
  }
  const by = new Map<string, NowRow[]>()
  for (const r of rows) {
    const k = r.projectId ?? '_'
    by.set(k, [...(by.get(k) ?? []), r])
  }
  return [...by.entries()]
    .sort(([a], [b]) => pos(a) - pos(b) || a.localeCompare(b))
    .map(([key, list]) => ({
      key,
      title: list[0]?.projectName ?? 'Sessions outside a project',
      hot: list.some((r) => r.family === 'attention'),
      rows: list.sort(
        (a, b) => FAMILY_ORDER.indexOf(a.family) - FAMILY_ORDER.indexOf(b.family) || byRecent(a, b),
      ),
    }))
}

export interface NowFilter {
  projectId?: string
  machineId?: string
  agent?: 'claude' | 'codex'
  family?: NowFamily
}
export function filterNowRows(rows: readonly NowRow[], f: NowFilter): NowRow[] {
  return rows.filter(
    (r) =>
      (!f.projectId || r.projectId === f.projectId) &&
      (!f.machineId || r.machineId === f.machineId) &&
      (!f.agent || r.agent === f.agent) &&
      (!f.family || r.family === f.family),
  )
}

// ---- keyboard triage (Now and Inbox) ------------------------------------------------------------

export interface TriageState {
  /** Focusable row keys in display order. */
  keys: readonly string[]
  focus: number
  selected: ReadonlySet<string>
  /** `x` anchor for ⇧x range selection. */
  anchor?: number
}

export type TriageAction =
  | { type: 'move'; index: number }
  | { type: 'open'; key: string }
  | { type: 'close' }
  | { type: 'pick'; key: string; n: number }
  | { type: 'suggested'; key: string }
  | { type: 'reply'; key: string }
  | { type: 'delegate'; key: string }
  | { type: 'snooze'; keys: string[] }
  | { type: 'select'; keys: string[]; anchor: number }
  | { type: 'project'; key: string }
  | { type: 'help' }

/**
 * j/k move · Enter opens · Esc closes · 1–9 quick-pick · s suggested · r reply · d decide for me ·
 * e snooze (the selection, else the focused row) · x select · ⇧x range · g p project · ? help.
 * `gp` is the chord as one key (the caller tracks the `g`).
 */
export function triageKey(
  k: { key: string; shift?: boolean },
  s: TriageState,
): TriageAction | null {
  const { key } = k
  if (key === '?') return { type: 'help' }
  if (key === 'Escape') return { type: 'close' }
  if (!s.keys.length) return null
  const focus = Math.min(Math.max(s.focus, 0), s.keys.length - 1)
  const cur = s.keys[focus] as string
  switch (key) {
    case 'j':
    case 'ArrowDown':
      return { type: 'move', index: Math.min(s.keys.length - 1, s.focus + 1) }
    case 'k':
    case 'ArrowUp':
      return { type: 'move', index: Math.max(0, s.focus - 1) }
    case 'Enter':
      return { type: 'open', key: cur }
    case 's':
      return { type: 'suggested', key: cur }
    case 'r':
      return { type: 'reply', key: cur }
    case 'd':
      return { type: 'delegate', key: cur }
    case 'e':
      return { type: 'snooze', keys: s.selected.size ? [...s.selected] : [cur] }
    case 'gp':
      return { type: 'project', key: cur }
    case 'x':
      if (!k.shift) {
        const next = new Set(s.selected)
        if (next.has(cur)) next.delete(cur)
        else next.add(cur)
        return { type: 'select', keys: [...next], anchor: focus }
      }
      return range(s, focus)
    case 'X':
      return range(s, focus)
  }
  if (/^[1-9]$/.test(key)) return { type: 'pick', key: cur, n: Number(key) }
  return null
}

function range(s: TriageState, focus: number): TriageAction {
  const a = s.anchor ?? focus
  const [lo, hi] = a <= focus ? [a, focus] : [focus, a]
  const next = new Set(s.selected)
  for (let i = lo; i <= hi; i++) next.add(s.keys[i] as string)
  return { type: 'select', keys: s.keys.filter((x) => next.has(x)), anchor: a }
}
